import { useEffect, useState } from "react";
import { Alert, Box, Checkbox, CircularProgress, Paper, Typography } from "@mui/material";

import apiClient from "../api-client";
import { Button } from "./Button";
import { useAuth } from "./contexts/AuthContext";

const FREE_MAX_CLIENTS = 3;

export function FreeClientSelectionGate() {
  const { user, interceptorReady, refreshUser } = useAuth();
  const plan = user?.agencyPlan;
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const mandatory = Boolean(plan?.requires_client_selection);
  const visible = Boolean(
    interceptorReady && plan?.selection_available && !dismissed && user?.role === "agency_admin"
  );

  useEffect(() => {
    if (!visible) return;
    let active = true;
    setLoading(true);
    apiClient.get("clients/?archived=false&page_size=100")
      .then(({ data }) => {
        if (!active) return;
        const rows = Array.isArray(data) ? data : (data?.results || []);
        setClients(rows);
        setSelected(rows.slice(0, FREE_MAX_CLIENTS).map((client) => client.id));
      })
      .catch(() => active && setError("No se ha podido cargar la lista de clientes."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [visible]);

  if (!visible) return null;

  const toggleClient = (clientId) => {
    setError("");
    setSelected((current) => current.includes(clientId)
      ? current.filter((id) => id !== clientId)
      : current.length < FREE_MAX_CLIENTS ? [...current, clientId] : current
    );
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await apiClient.post(`agencies/${user.agencyId}/free-clients-selection/`, {
        client_ids: selected,
      });
      await refreshUser();
    } catch (requestError) {
      setError(requestError.response?.data?.client_ids || "No se ha podido guardar la selección.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: 200000, bgcolor: "rgba(15,23,42,.72)", display: "grid", placeItems: "center", p: 2 }}>
      <Paper sx={{ width: "100%", maxWidth: 620, maxHeight: "90vh", overflow: "auto", p: 3, borderRadius: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Elige los {FREE_MAX_CLIENTS} clientes que podrás gestionar</Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Tendrás acceso completo a los clientes elegidos. Los demás seguirán visibles con todos sus datos, proyectos y contenidos, pero en modo solo lectura.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{String(error)}</Alert>}
        {loading ? <Box sx={{ display: "grid", placeItems: "center", py: 4 }}><CircularProgress /></Box> : (
          <Box sx={{ display: "grid", gap: 1, mb: 3 }}>
            {clients.map((client) => (
              <Box
                key={client.id}
                onClick={() => toggleClient(client.id)}
                sx={{ display: "flex", alignItems: "center", p: 1.25, border: "1px solid", borderColor: selected.includes(client.id) ? "primary.main" : "divider", borderRadius: 2, cursor: "pointer" }}
              >
                <Checkbox checked={selected.includes(client.id)} />
                <Typography>{client.name}</Typography>
              </Box>
            ))}
          </Box>
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          {!mandatory && <Button variant="ghost" onClick={() => setDismissed(true)}>Ahora no</Button>}
          <Button onClick={save} disabled={loading || saving || selected.length !== FREE_MAX_CLIENTS}>
            {saving ? "Guardando…" : `Guardar selección (${selected.length}/${FREE_MAX_CLIENTS})`}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
